import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import type { BadgeTone } from '@/components/admin/Badge';
import { Button } from '@/components/ui/button';
import { useDrawerEditor } from '@/features/sessions/components/workspace/DetailDrawer/SessionPanel/DrawerEditorContext';
import { cn } from '@/lib/utils';

import type { AutosaveState, EditorKind } from './ClinicalEditorShell';

// Editorial status pill — used in place of the generic admin Badge here
// because the topbar wants a tighter, italic, mono-tinted chip that sits
// next to the editorial title strip. The dot doubles as a status affordance.
const PILL_TONE: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-100 text-neutral-600 ring-neutral-200/70',
  primary: 'bg-primary-50 text-primary-700 ring-primary-200/60',
  info: 'bg-[#e8f0f5] text-accent-ink ring-[#cdd9e2]/60',
  warning: 'bg-warning-50 text-warning-700 ring-warning-300/70',
  success: 'bg-[#e8f3ec] text-[#1b6a3a] ring-[#cae0d3]/60',
  danger: 'bg-[#fdecec] text-error-700 ring-[#f1c1c1]/60',
};
const PILL_DOT: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-400',
  primary: 'bg-primary-500',
  info: 'bg-accent-ink/70',
  warning: 'bg-warning-500',
  success: 'bg-[#2f8a55]',
  danger: 'bg-error-500',
};

interface StatusPillProps {
  tone: BadgeTone;
  label: string;
}

function StatusPill({ tone, label }: StatusPillProps) {
  return (
    <span
      className={cn(
        'tracking-label inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1',
        PILL_TONE[tone],
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', PILL_DOT[tone])} />
      {label}
    </span>
  );
}

interface Props {
  kind: EditorKind;
  clientName: string;
  scheduledStart: string | null;
  status: string;
  statusTone: BadgeTone;
  statusLabel: string;
  /** Kept in the API for compatibility — rendered only inside `EditorDocumentHeader` now. */
  authorLabel: string;
  /** Kept in the API for compatibility — rendered only inside `EditorDocumentHeader` now. */
  autosaveState: AutosaveState;
  /** Kept in the API for compatibility — rendered only inside `EditorDocumentHeader` now. */
  wordCount: number;
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'primary' | 'destructive';
  };
  secondaryAction?: { label: string; onClick: () => void; disabled?: boolean };
  // clientId is null on anonymous helpline sessions; the back-link then drops the client scope.
  backTo: { sessionId: string; clientId: string | null; drawerTab: 'notes' | 'case-reports' };
}

const formatShortDate = (iso: string | null): string => {
  if (iso === null) return '';
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
};

export function EditorTopbar({
  kind,
  clientName,
  scheduledStart,
  statusTone,
  statusLabel,
  primaryAction,
  secondaryAction,
  backTo,
  // The three values below are now rendered by EditorDocumentHeader. We
  // intentionally accept them so the composers (SessionNoteEditor /
  // CaseReportEditor) don't need parallel wiring changes — they pass the
  // shell context into both surfaces.
  authorLabel: _authorLabel,
  autosaveState: _autosaveState,
  wordCount: _wordCount,
}: Props) {
  const drawerEditor = useDrawerEditor();
  const kindLabel = kind === 'note' ? 'Notes' : 'Case report';
  const dateTime = formatShortDate(scheduledStart);

  return (
    <header
      // Slim editorial topbar — a single hairline border-b separates the
      // topbar from the toolbar. No shadow or backdrop-blur; the writing
      // surface owns visual weight, the chrome stays out of the way.
      className="border-accent-ink/10 sticky top-0 z-10 flex h-12 items-center justify-between gap-3 border-b bg-white px-4"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {drawerEditor !== null ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Back to workspace"
            className="text-accent-ink/70 hover:bg-primary-50 hover:text-accent-ink h-7 w-7 rounded-full"
            onClick={() => {
              drawerEditor.exitToTab(backTo.drawerTab);
            }}
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label="Back to workspace"
            className="text-accent-ink/70 hover:bg-primary-50 hover:text-accent-ink h-7 w-7 rounded-full"
          >
            <Link
              to="/app/sessions"
              search={{
                client: backTo.clientId ?? undefined,
                panel: 'session' as const,
                id: backTo.sessionId,
                drawerTab: backTo.drawerTab,
                segment: 'all' as const,
              }}
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Link>
          </Button>
        )}
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="font-display text-accent-ink truncate text-[13px] italic">
            {kindLabel}
          </span>
          <span aria-hidden className="text-accent-ink/25 text-[11px]">
            ·
          </span>
          <span className="truncate text-[12.5px] font-medium text-neutral-700">{clientName}</span>
          {dateTime !== '' ? (
            <>
              <span aria-hidden className="text-accent-ink/25 text-[11px]">
                ·
              </span>
              <span className="font-mono text-[10px] tracking-[0.05em] text-neutral-500 uppercase">
                {dateTime}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <StatusPill tone={statusTone} label={statusLabel} />
        {secondaryAction !== undefined ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled === true}
            className="h-7 rounded-full px-3 text-[12px]"
          >
            {secondaryAction.label}
          </Button>
        ) : null}
        {primaryAction !== undefined ? (
          <Button
            variant={primaryAction.variant === 'destructive' ? 'destructive' : 'primary'}
            size="sm"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled === true}
            // Compact pill primary CTA — readable but in proportion with
            // the slimmer topbar.
            className="h-7 rounded-full px-3 text-[12px] font-medium"
          >
            {primaryAction.label}
          </Button>
        ) : null}
      </div>
    </header>
  );
}
